import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  alertRuleResponseSchema,
  type AlertRuleResponse,
} from "@community-analytics/shared";
import { z } from "zod";

import { apiFetch } from "@shared/api";

import { alertRuleQueryKeys } from "./keys";

const alertRulesListSchema = z.array(alertRuleResponseSchema);

/**
 * GET /api/communities/:communityId/alert-rules.
 *
 * The hook is a no-op when `communityId` is null (the active-community
 * store hasn't picked one yet). Re-validates the response shape against
 * the shared schema so a backend drift fails loudly in tests instead of
 * silently rendering malformed UI.
 */
export function useAlertRulesQuery(
  communityId: string | null,
): UseQueryResult<AlertRuleResponse[], Error> {
  return useQuery<AlertRuleResponse[], Error>({
    queryKey: alertRuleQueryKeys.list(communityId ?? "__noop__"),
    enabled: communityId !== null,
    queryFn: async () => {
      if (communityId === null) {
        // Unreachable in production — `enabled` gates the call.
        throw new Error("communityId is required");
      }
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/alert-rules/`,
      );
      return alertRulesListSchema.parse(raw);
    },
  });
}
